import { useReducer, useRef, useState, useCallback } from "react";
import { apiFetch } from "@shared/api-client";
import { isValidDate, isValidTime } from "@shared/validators";
import { track } from "@/lib/analytics";
import {
  isValidPackExtractionShape,
  type PackExtraction,
} from "./tech-rider-types";
import { validateTechRiderFile } from "./validators";
import { packAnswerSupported } from "./active-questions";
import type { InquiryProfileAnswers, PackageFormData } from "./types";
import {
  type InquiryPackQuestionConfig,
  type InquiryProfileConfig,
} from "./inquiry-profile-schema";

/**
 * Single FSM for tech-rider parsing. Was previously split between a
 * `parseStateRef` (read in callbacks) and a `parseStatus` state (drove
 * rendering) — two writes per transition + drift risk. One reducer
 * covers both needs.
 *
 * NOTE: the background parse hits `POST /api/parse-tech-rider` via apiFetch
 * (which owns the base URL and the tenant header).
 * If parsing fails, the FSM lands in `error`, the wizard shows the "we
 * couldn't read your file" notice, and the raw file is still uploaded with
 * the inquiry submission so the operator can read it manually. Autofill is
 * the only thing lost.
 *
 * Profile-driven extraction (#58): the POST carries `profileKey` and the
 * backend derives the prompt and schema from that profile's declared
 * questions. One response shape for every profile — a PackExtraction.
 */
type ParseState =
  | { status: "idle" }
  | { status: "parsing"; promise: Promise<PackExtraction | null> }
  | { status: "done"; data: PackExtraction }
  | { status: "error" };

type ParseAction =
  | { type: "reset" }
  | { type: "start"; promise: Promise<PackExtraction | null> }
  | { type: "success"; data: PackExtraction }
  | { type: "error" };

function parseReducer(_state: ParseState, action: ParseAction): ParseState {
  switch (action.type) {
    case "reset":
      return { status: "idle" };
    case "start":
      return { status: "parsing", promise: action.promise };
    case "success":
      return { status: "done", data: action.data };
    case "error":
      return { status: "error" };
  }
}

/**
 * The core event fields both extraction shapes carry, keyed identically on
 * PackExtraction and TechRiderRequirements. `eventType` is rider-only — the
 * pack prompt doesn't ask for it.
 */
interface CoreEventExtraction {
  eventName?: string | null;
  organization?: string | null;
  eventDate?: string | null;
  eventEndDate?: string | null;
  eventStartTime?: string | null;
  eventEndTime?: string | null;
  location?: string | null;
  attendance?: number | null;
  indoorOutdoor?: string | null;
  eventType?: string | null;
}

/**
 * Write the core event/venue fields from an extraction onto `updates`,
 * never overwriting what the customer has already answered. Shared by both
 * paths so a rider and a pack extraction pre-fill the wizard identically.
 *
 * Applying here is safe because the parse resolves on leaving the Documents
 * step, before the customer reaches Event/Venue — so a run sheet fills those
 * steps instead of contradicting them.
 */
function applyCoreEventFields(
  parsed: CoreEventExtraction,
  current: Partial<PackageFormData> | undefined,
  updates: Partial<PackageFormData>,
  filled: Set<string>,
  eventTypeKeys: readonly string[],
): void {
  const fillCore = <K extends keyof PackageFormData>(
    field: K,
    value: PackageFormData[K] | undefined,
  ): void => {
    if (value === undefined) return;
    const existing = current?.[field];
    if (existing !== undefined && existing !== null && existing !== "") return;
    updates[field] = value;
    filled.add(String(field));
  };

  const cleanString = (v: string | null | undefined): string | undefined => {
    const trimmed = v?.trim();
    return trimmed ? trimmed : undefined;
  };

  fillCore("eventName", cleanString(parsed.eventName));
  fillCore("organization", cleanString(parsed.organization));
  const eventType = cleanString(parsed.eventType);
  fillCore(
    "eventType",
    eventType && eventTypeKeys.includes(eventType) ? eventType : undefined,
  );
  const startDate = cleanString(parsed.eventDate);
  fillCore(
    "eventDate",
    startDate && isValidDate(startDate) ? startDate : undefined,
  );
  const endDate = cleanString(parsed.eventEndDate);
  fillCore(
    "eventEndDate",
    endDate && isValidDate(endDate) && !(startDate && endDate < startDate)
      ? endDate
      : undefined,
  );
  const startTime = cleanString(parsed.eventStartTime);
  fillCore(
    "eventStartTime",
    startTime && isValidTime(startTime) ? startTime : undefined,
  );
  const endTime = cleanString(parsed.eventEndTime);
  fillCore(
    "eventEndTime",
    endTime && isValidTime(endTime) ? endTime : undefined,
  );
  fillCore("location", cleanString(parsed.location));
  fillCore(
    "attendance",
    typeof parsed.attendance === "number" &&
      Number.isInteger(parsed.attendance) &&
      parsed.attendance > 0
      ? parsed.attendance
      : undefined,
  );
  const environment = cleanString(parsed.indoorOutdoor);
  fillCore(
    "indoorOutdoor",
    environment === "Indoor" || environment === "Outdoor"
      ? environment
      : undefined,
  );
}


/**
 * Map a pack extraction onto the `profileAnswers` bag (#58). Pure and
 * per-answer never-overwrite: an extracted value only lands on a question
 * the user hasn't answered yet, so this returns an already-merged
 * `profileAnswers` — callers apply it verbatim, no flat-field filtering.
 * `filled` carries the question ids that were actually written (notice set),
 * plus any core event fields and `additionalInfo` when the notes landed.
 *
 * Core event fields (name, dates, times, location, attendance,
 * indoor/outdoor) also land here — the parse applies on leaving the
 * Documents step, before the customer reaches Event/Venue, so a run sheet
 * fills those steps instead of contradicting them.
 */
export function derivePackUpdates(
  parsed: PackExtraction,
  packQuestions: readonly InquiryPackQuestionConfig[],
  current?: Partial<PackageFormData>,
  eventTypeKeys: readonly string[] = [],
): { updates: Partial<PackageFormData>; filled: Set<string> } {
  const updates: Partial<PackageFormData> = {};
  const filled = new Set<string>();

  applyCoreEventFields(parsed, current, updates, filled, eventTypeKeys);

  // The technical block, when the profile opted into it: the artist name
  // prefills bandNames, and requested gear is prepended to the notes.
  const rider = parsed.rider;
  if (rider?.artistName && !current?.bandNames) {
    updates.bandNames = rider.artistName;
    filled.add("bandNames");
  }

  const existing: InquiryProfileAnswers = current?.profileAnswers ?? {};
  const merged: InquiryProfileAnswers = { ...existing };
  let answersWritten = 0;
  for (const question of packQuestions) {
    const value = parsed.fields[question.id];
    if (value === undefined || value === "") continue;
    const already = existing[question.id];
    if (already !== undefined && already !== "") continue;
    // The rule a sent answer is held to: a parse that answers a checkbox
    // with words, or a select with a value it does not offer, writes
    // nothing rather than an answer the form could never send.
    if (!packAnswerSupported(question, value)) continue;
    merged[question.id] = value;
    filled.add(question.id);
    answersWritten += 1;
  }
  // Only when an answer actually landed — `filled` also carries core event
  // fields, which must not conjure an otherwise-empty bag.
  if (answersWritten > 0) {
    updates.profileAnswers = merged;
  }

  const gear = rider?.specificGear ?? [];
  const notes = [
    gear.length > 0 ? `Gear requests: ${gear.join(", ")}` : "",
    parsed.additionalNotes?.trim() ?? "",
  ]
    .filter(Boolean)
    .join("\n\n");
  if (notes && !current?.additionalInfo) {
    updates.additionalInfo = notes;
    filled.add("additionalInfo");
  }

  return { updates, filled };
}

/**
 * Exactly what the wizard consumes (#334 item 4) — don't widen this without
 * a consumer. The applied-document value and the full FSM status are
 * internals; the wizard only needs to know whether the parse failed.
 */
export interface UseTechRiderParser {
  file: File | null;
  fileError: string | null;
  isApplying: boolean;
  autofilledFields: Set<string>;
  /** True when the background parse failed — the wizard shows the
   *  "we couldn't read your file" notice; the raw file still uploads. */
  parseFailed: boolean;
  setFile: (file: File | null) => void;
  applyIfReady: (
    current?: Partial<PackageFormData>,
  ) => Promise<Partial<PackageFormData> | null>;
  getParsed: () => PackExtraction | null;
}

export function useTechRiderParser(
  profile: InquiryProfileConfig,
): UseTechRiderParser {
  const [file, setFileState] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [autofilledFields, setAutofilledFields] = useState<Set<string>>(
    () => new Set(),
  );
  const [appliedDocument, setAppliedDocument] =
    useState<PackExtraction | null>(null);
  const [parseState, dispatch] = useReducer(parseReducer, { status: "idle" });
  const parseRunRef = useRef(0);
  // The profile drives which response shape is expected; keep the latest in
  // a ref so in-flight parse callbacks see the profile active at call time.
  const profileRef = useRef(profile);
  profileRef.current = profile;

  const startBackgroundParsing = useCallback((target: File, runId: number): void => {
    const formDataObj = new FormData();
    formDataObj.append("file", target);
    // Lets the backend derive the profile's extraction schema (#58);
    // mirrors submit-inquiry.ts. An absent or unknown key means the
    // backend has nothing to extract against and answers null.
    formDataObj.append("profileKey", profileRef.current.key);

    // apiFetch, not raw fetch: the tenant header must ride this request or
    // the backend resolves the wrong Company's profile and extracts against
    // the wrong question set.
    const promise: Promise<PackExtraction | null> = apiFetch<unknown>(
      "/api/parse-tech-rider",
      { method: "POST", body: formDataObj },
    )
      .then((data: unknown): PackExtraction | null => {
        if (parseRunRef.current !== runId) return null;
        const parsed = isValidPackExtractionShape(data) ? data : null;
        if (!parsed) {
          track("tech_rider_parse_failed", { profileKey: profileRef.current.key });
          dispatch({ type: "error" });
          return null;
        }
        track("tech_rider_parsed", { profileKey: profileRef.current.key });
        dispatch({ type: "success", data: parsed });
        return parsed;
      })
      .catch((err: unknown) => {
        if (parseRunRef.current !== runId) return null;
        console.error("Background tech rider parsing failed:", err);
        track("tech_rider_parse_failed", { profileKey: profileRef.current.key });
        dispatch({ type: "error" });
        return null;
      });

    dispatch({ type: "start", promise });
  }, []);

  const setFile = useCallback(
    (next: File | null): void => {
      const runId = parseRunRef.current + 1;
      parseRunRef.current = runId;
      setFileError(null);
      dispatch({ type: "reset" });
      setAppliedDocument(null);
      setAutofilledFields(new Set());

      if (!next) {
        setFileState(null);
        return;
      }

      const validation = validateTechRiderFile(next);
      if (!validation.ok) {
        setFileError(validation.message);
        setFileState(null);
        return;
      }

      setFileState(next);
      track("tech_rider_uploaded", { profileKey: profileRef.current.key });
      startBackgroundParsing(next, runId);
    },
    [startBackgroundParsing],
  );

  const applyIfReady = useCallback(
    async (
      current?: Partial<PackageFormData>,
    ): Promise<Partial<PackageFormData> | null> => {
      if (!file || appliedDocument) return null;

      setIsApplying(true);
      try {
        let parsed: PackExtraction | null = null;
        if (parseState.status === "done") {
          parsed = parseState.data;
        } else if (parseState.status === "parsing") {
          parsed = await parseState.promise;
        }

        if (!parsed) return null;

        // derivePackUpdates already merges per-answer and never
        // overwrites — its updates apply verbatim.
        const { updates, filled } = derivePackUpdates(
          parsed,
          profileRef.current.contentStep.packQuestions,
          current,
          profileRef.current.eventDetailsStep.eventTypeOptions.map(
            ({ value }) => value,
          ),
        );
        setAppliedDocument(parsed);
        setAutofilledFields(filled);
        return updates;
      } catch (error) {
        console.error("Tech rider parsing failed:", error);
        return null;
      } finally {
        setIsApplying(false);
      }
    },
    [file, appliedDocument, parseState],
  );

  const getParsed = useCallback((): PackExtraction | null => {
    if (appliedDocument) return appliedDocument;
    return parseState.status === "done" ? parseState.data : null;
  }, [appliedDocument, parseState]);

  return {
    file,
    fileError,
    isApplying,
    autofilledFields,
    parseFailed: parseState.status === "error",
    setFile,
    applyIfReady,
    getParsed,
  };
}
